import { useEffect, useRef, useState, useMemo } from "react";
import { PageHeader } from "@/components/PageHeader/PageHeader.js";
import { SearchBar, Select, type Column } from "@legal-500/legal500-design-system";
import { DataTable } from "@/components/DataTable/DataTable.js";
import { PaginationControls } from "@/components/PaginationControls/PaginationControls.js";
import LoadingState from "@/components/LoadingState/index.js";
import EmptyState from "@/components/EmptyState/index.js";
import { useQuery } from "@/hooks/useApi.js";
import { docParsing, type ActivityLogEntry } from "@/api/docParsing.service.js";
import ParsingJobsPage from "@/pages/Submissions/ParsingJobs/index.js";
import styles from "@/pages/Submissions/ParsingJobs/ParsingJobs.module.css";
import monitoringStyles from "./ParseMonitoring.module.css";

function QueueStatsCard() {
  const { data, refetch } = useQuery(() => docParsing.getQueueStats(), []);

  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  useEffect(() => {
    const interval = setInterval(() => refetchRef.current(), 10_000);
    return () => clearInterval(interval);
  }, []);

  const queue = data?.queue ?? null;
  const counts = queue?.counts;

  const countItems: Array<{ label: string; key: keyof NonNullable<typeof counts> }> = [
    { label: "Waiting", key: "waiting" },
    { label: "Active", key: "active" },
    { label: "Completed", key: "completed" },
    { label: "Failed", key: "failed" },
    { label: "Delayed", key: "delayed" },
    { label: "Paused", key: "paused" },
  ];

  return (
    <div className={styles.queueStatsCard}>
      <div className={styles.queueStatsHeader}>
        <h3 className={styles.queueStatsTitle}>{queue?.name ?? "Queue Stats"}</h3>
        {queue?.lastExecutedAt != null && (
          <span className={styles.queueStatsLastRun}>
            Last run: {new Date(queue.lastExecutedAt).toLocaleString()}
          </span>
        )}
      </div>
      <div className={styles.queueCountsGrid}>
        {countItems.map(({ label, key }) => {
          const value = counts?.[key];
          const isFailed = key === "failed" && value != null && value > 0;
          return (
            <div key={key} className={styles.queueCountItem}>
              <span className={styles.queueCountLabel}>{label}</span>
              <span
                className={`${styles.queueCountValue}${isFailed ? ` ${styles.queueCountFailed}` : ""}`}
              >
                {value != null ? value : "\u2014"}
              </span>
            </div>
          );
        })}
      </div>
      {queue?.recentFailedJobs != null && queue.recentFailedJobs.length > 0 && (
        <div className={styles.queueJobsSection}>
          <h4 className={styles.queueJobsSectionTitle}>Recent Failed Jobs</h4>
          <ul className={styles.queueJobsList}>
            {queue.recentFailedJobs.map(job => {
              const reason = job.failedReason ?? "Unknown reason";
              const truncatedReason = reason.length > 120 ? `${reason.slice(0, 120)}...` : reason;
              return (
                <li key={job.id} className={styles.queueJobItem}>
                  <div className={styles.queueJobRow}>
                    <span className={styles.queueJobName}>{job.name}</span>
                    <span className={styles.queueJobMeta}>
                      {job.attemptsMade} attempt{job.attemptsMade !== 1 ? "s" : ""}
                    </span>
                    {job.finishedOn != null && (
                      <span className={styles.queueJobTime}>
                        {new Date(job.finishedOn).toLocaleString()}
                      </span>
                    )}
                  </div>
                  <div className={styles.queueJobReason} title={reason}>
                    {truncatedReason}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {queue?.activeJobs != null && queue.activeJobs.length > 0 && (
        <div className={styles.queueJobsSection}>
          <h4 className={styles.queueJobsSectionTitle}>Active Jobs</h4>
          <ul className={styles.queueJobsList}>
            {queue.activeJobs.map(job => (
              <li key={job.id} className={styles.queueJobItem}>
                <div className={styles.queueJobRow}>
                  <span className={styles.queueJobName}>{job.name}</span>
                  {job.processedOn != null && (
                    <span className={styles.queueJobMeta}>
                      Started: {new Date(job.processedOn).toLocaleString()}
                    </span>
                  )}
                  {job.timestamp != null && (
                    <span className={styles.queueJobTime}>
                      Created: {new Date(job.timestamp).toLocaleString()}
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className={styles.queueStatsFooter}>
        <span className={styles.queueRefreshDot} />
        Auto-refreshing every 10s
      </div>
    </div>
  );
}

type BarStatVariant = "success" | "warning" | "danger" | "accent";

function percentOf(value: number, total: number): number | null {
  if (total === 0) return null;
  return Math.round((value / total) * 100);
}

function BarStatCard({
  label,
  value,
  total,
  variant,
}: {
  label: string;
  value: number | null;
  total: number | null;
  variant?: BarStatVariant;
}) {
  const percentage =
    value != null && total != null && variant !== undefined ? percentOf(value, total) : null;

  return (
    <div className={monitoringStyles.statCard}>
      <p className={monitoringStyles.statLabel}>{label}</p>
      <div className={monitoringStyles.statValueRow}>
        <p className={monitoringStyles.statValue}>
          {value != null ? value.toLocaleString() : "\u2014"}
        </p>
        {percentage !== null && variant !== undefined && (
          <span className={`${monitoringStyles.statPct} ${monitoringStyles[variant]}`}>
            {percentage}%
          </span>
        )}
      </div>
    </div>
  );
}

// Parse outcome of the UK Bar 2027 template, from doc_parse_results (not the queue, so the numbers
// survive Redis retention). Percentages are over the UK Bar total; the footnote carries the two
// counts that sit outside that base.
function BarStatsCard() {
  const { data, error } = useQuery(() => docParsing.getBarStats(), []);

  const stats = data?.stats ?? null;
  const ukBar = stats?.ukBar2027 ?? null;
  const total = ukBar?.total ?? null;

  let footnote = "Loading\u2026";
  if (error) {
    footnote = "Could not load Bar parsing statistics.";
  } else if (stats) {
    footnote =
      `Other Bar-family templates (bar_family_unknown): ${stats.barFamilyUnknown.toLocaleString()}` +
      ` \u00b7 Failed with no parser output, template unknown: ${stats.failedWithoutParsedJson.toLocaleString()}`;
  }

  return (
    <div className={monitoringStyles.section}>
      <h3 className={monitoringStyles.sectionTitle}>UK Bar 2027 parsing</h3>
      <div className={monitoringStyles.statGrid}>
        <BarStatCard label="UK Bar 2027 files" value={total} total={null} />
        <BarStatCard label="Clean" value={ukBar?.clean ?? null} total={total} variant="success" />
        <BarStatCard
          label="Minor review"
          value={ukBar?.minor ?? null}
          total={total}
          variant="warning"
        />
        <BarStatCard
          label="Major review"
          value={ukBar?.major ?? null}
          total={total}
          variant="danger"
        />
        <BarStatCard label="Failed" value={ukBar?.failed ?? null} total={total} variant="danger" />
        <BarStatCard
          label="In progress"
          value={ukBar?.inProgress ?? null}
          total={total}
          variant="accent"
        />
      </div>
      <p className={monitoringStyles.statFootnote}>{footnote}</p>
    </div>
  );
}

const DEFAULT_LOG_PAGE_SIZE = 20;

function ActivityLogSection() {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_LOG_PAGE_SIZE);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, loading } = useQuery(
    () =>
      docParsing.listActivityLogs({
        resourceType: "doc-parsing-file",
        search: debouncedSearch || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
    [debouncedSearch, page, pageSize],
  );

  const logs = data?.results ?? [];
  const total = data?.pagination.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + logs.length;

  const columns: Column<ActivityLogEntry>[] = useMemo(
    () => [
      {
        header: "Time",
        accessor: "createdAt" as keyof ActivityLogEntry,
        render: (value: unknown) => new Date(value as string).toLocaleString(),
      },
      {
        header: "Action",
        accessor: "action" as keyof ActivityLogEntry,
        render: (value: unknown) => (
          <span className={monitoringStyles.actionBadge}>{String(value)}</span>
        ),
      },
      {
        header: "Resource ID",
        accessor: "resourceId" as keyof ActivityLogEntry,
        render: (value: unknown) => {
          const id = String(value);
          return (
            <span
              className={monitoringStyles.copyableId}
              onClick={() => navigator.clipboard.writeText(id)}
            >
              {id}
            </span>
          );
        },
      },
      {
        header: "Actor",
        accessor: "actorId" as keyof ActivityLogEntry,
        render: (value: unknown, row: ActivityLogEntry) => {
          const actorId = String(value ?? "");
          const truncated = actorId.length > 12 ? `${actorId.slice(0, 12)}...` : actorId;
          return `${truncated} (${row.actorRole ?? "unknown"})`;
        },
      },
      {
        header: "Details",
        accessor: "details" as keyof ActivityLogEntry,
        render: (value: unknown) => {
          const details = value as Record<string, unknown> | null;
          if (!details || Object.keys(details).length === 0) return "—";
          return <span className={monitoringStyles.detailsCell}>{JSON.stringify(details)}</span>;
        },
      },
    ],
    [],
  );

  return (
    <div>
      <h3 className={monitoringStyles.sectionTitle}>Activity Log</h3>

      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search by file name or ID..."
        onClear={() => setSearch("")}
      />

      <div className={monitoringStyles.rowsPerPageControl}>
        <label htmlFor="logsPageSize" className={monitoringStyles.rowsPerPageLabel}>
          Rows per page:
        </label>
        <Select
          id="logsPageSize"
          value={pageSize}
          onChange={e => {
            setPageSize(Number(e.target.value));
            setPage(1);
          }}
          options={[
            { value: 10, label: "10" },
            { value: 20, label: "20" },
            { value: 50, label: "50" },
            { value: 100, label: "100" },
          ]}
          size="small"
        />
      </div>

      <div className={monitoringStyles.tableContainer}>
        {loading && <LoadingState message="Loading activity logs..." />}

        {!loading && logs.length === 0 && (
          <EmptyState
            variant="empty"
            title="No activity logs"
            message="No doc-parsing activity logs found."
          />
        )}

        {!loading && logs.length > 0 && (
          <>
            <DataTable data={logs} columns={columns} rowKey="id" size="small" />
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              startIndex={startIndex}
              endIndex={endIndex}
              totalCount={total}
              itemName="logs"
              onPageChange={setPage}
            />
          </>
        )}
      </div>
    </div>
  );
}

function ParseMonitoringPage() {
  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Parse Monitoring" />
      </div>
      <QueueStatsCard />
      <BarStatsCard />
      <ParsingJobsPage />
      <ActivityLogSection />
    </div>
  );
}

export default ParseMonitoringPage;
